import { Link } from 'react-router-dom'
import { ArrowRight, Instagram, Mail, MapPin, Phone } from 'lucide-react'
import PageHero from '../components/PageHero'
import alejandro from '../img/alejandro.png'
import janaina from '../img/janaina.png'
import valentina from '../img/valentina.png'

const values = [
  {
    title: 'Honestidad',
    text: 'El hándicap, la clasificación y la clase se dicen como son. La confianza de la comunidad depende de eso.',
  },
  {
    title: 'Perseverancia',
    text: 'El swing adaptado se construye repetición por repetición. Lo mismo el movimiento: de un hermano en 2012 a un open con cuatro países.',
  },
  {
    title: 'Resiliencia',
    text: 'El cuerpo cambia y el juego también. La asociación existe para que ese cambio no saque a nadie del campo.',
  },
]

const team = [
  {
    name: 'Alejandro Florero',
    role: 'Presidente y profesor',
    photo: alejandro,
    text: 'Da las clases gratuitas en Xochimilco y preside la asociación desde que el movimiento empezó en 2012.',
    links: [
      { label: 'alejandroflorero4@gmail.com', href: 'mailto:alejandroflorero4@gmail.com' },
      { label: '55 2378 5783', href: 'tel:+525523785783' },
    ],
  },
  {
    name: 'Janaina Gazzatte Chendi',
    role: 'Directora general',
    photo: janaina,
    text: 'Maestra en preparación física del golfista adaptado. Acompaña el entrenamiento más allá de la clase en el campo.',
    links: [
      { label: 'janainagazzatte@gmail.com', href: 'mailto:janainagazzatte@gmail.com' },
      { label: '55 3077 1542', href: 'tel:+525530771542' },
      { label: '@pf_janagazzatte', href: 'https://www.instagram.com/pf_janagazzatte' },
    ],
  },
  {
    name: 'Valentina Cuevas',
    role: 'Relaciones públicas',
    photo: valentina,
    text: 'Enlace para medios, alianzas y quien quiera difundir el golf adaptado.',
    links: [
      { label: 'emddeportivo@gmail.com', href: 'mailto:emddeportivo@gmail.com' },
      { label: '555637280273', href: 'tel:555637280273' },
    ],
  },
]

const contactKind = (href) => {
  if (href.startsWith('mailto:')) return { icon: Mail, label: 'Correo' }
  if (href.startsWith('tel:')) return { icon: Phone, label: 'Llamar' }
  return { icon: Instagram, label: 'Instagram' }
}

const About = () => (
  <div className="min-h-screen bg-ivory">
    <PageHero
      eyebrow="Asociación Nacional de Golf Adaptado A.C."
      title="El golf como"
      highlight="herramienta"
      description="Atendemos sin distinción y usamos el juego para la rehabilitación física y psicológica."
    />

    <section className="section-pad mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
      <article className="rounded-[2rem] bg-white p-8 ring-1 ring-leaf/10 md:p-12">
        <p className="eyebrow">Misión</p>
        <p className="mt-5 font-serif text-3xl leading-snug text-ink md:text-4xl">
          Atender a las personas con discapacidad, sin distinción, usando el golf como rehabilitación física y psicológica.
        </p>
      </article>
      <article className="rounded-[2rem] bg-fairway p-8 text-cream md:p-12">
        <p className="eyebrow text-sun">Visión</p>
        <p className="mt-5 font-serif text-3xl leading-snug md:text-4xl">
          Rehabilitar y reinsertar en la comunidad a quien vive con una discapacidad, con bienestar social y familiar.
        </p>
      </article>
    </section>

    <section className="bg-cream">
      <div className="section-pad mx-auto grid max-w-7xl items-start gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">Desde 2012</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">Un deporte que en México era de pocos.</h2>
        </div>
        <div className="space-y-5 text-lg leading-relaxed text-mist lg:col-span-7">
          <p>
            El golf llegó al país a finales del siglo XIX, practicado sobre todo por estadounidenses, ingleses y unos cuantos mexicanos con recursos. Durante mucho tiempo fue un privilegio.
          </p>
          <p>
            En 2012, Alejandro Florero buscó otra forma de jugar cuando su hermano recibió un diagnóstico de Parkinson. Ahí empieza el golf adaptado en México. Hoy hay clases en Xochimilco y seguimiento en ocho estados.
          </p>
          <p>
            El marco ya no es local. Desde 2023 la Regla 25 forma parte de las Reglas de Golf, y en 2026 el Club de Golf México recibió el I Campeonato Nacional con 25 jugadores de cuatro países.
          </p>
          <Link to="/history" className="inline-flex items-center gap-1 font-semibold text-leaf">
            Recorrer la historia
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>

    <section className="border-y border-leaf/10 bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-y divide-leaf/10 sm:grid-cols-4 sm:divide-y-0">
        {[
          ['2012', 'Inicio en México'],
          ['2024', 'Primer torneo nacional'],
          ['2026', 'Adaptive Open, 25 jugadores'],
          ['8', 'Estados con apoyo'],
        ].map(([value, label]) => (
          <div key={label} className="px-4 py-10 text-center">
            <p className="font-serif text-3xl text-leaf md:text-4xl">{value}</p>
            <p className="mt-1 text-sm text-mist">{label}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="section-pad mx-auto max-w-7xl">
      <p className="eyebrow">Valores</p>
      <h2 className="display mt-4 text-4xl md:text-5xl">Cómo trabajamos.</h2>
      <div className="mt-12 grid gap-8 md:grid-cols-3">
        {values.map((value, index) => (
          <article key={value.title}>
            <p className="font-serif text-sm tracking-eyebrow text-leaf">0{index + 1}</p>
            <h3 className="mt-3 font-serif text-2xl">{value.title}</h3>
            <p className="mt-3 leading-relaxed text-mist">{value.text}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="bg-cream">
      <div className="section-pad mx-auto max-w-7xl">
        <p className="eyebrow">Equipo</p>
        <h2 className="display mt-4 text-4xl md:text-5xl">Quién sostiene la cancha.</h2>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {team.map((member) => (
            <article key={member.name} className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
              <img
                src={member.photo}
                alt={member.name}
                className="aspect-square w-full rounded-2xl object-cover object-center"
              />
              <p className="mt-5 text-sm font-semibold text-leaf">{member.role}</p>
              <h3 className="mt-2 font-serif text-2xl">{member.name}</h3>
              <p className="mt-3 flex-1 leading-relaxed text-mist">{member.text}</p>
              <ul className="mt-6 flex flex-wrap gap-2 border-t border-leaf/10 pt-5">
                {member.links.map((link) => {
                  const kind = contactKind(link.href)
                  const Icon = kind.icon
                  return (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        aria-label={`${kind.label}: ${link.label}`}
                        title={link.label}
                        className="inline-flex items-center gap-2 rounded-full bg-cream px-3.5 py-2 text-sm font-semibold text-leaf transition-colors hover:bg-leaf hover:text-white"
                        {...(link.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {kind.label}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="section-pad mx-auto max-w-7xl">
      <div className="grid gap-5 md:grid-cols-3">
        {[
          { icon: Mail, label: 'Email', value: 'alejandroflorero4@gmail.com', href: 'mailto:alejandroflorero4@gmail.com' },
          { icon: Phone, label: 'Teléfono', value: '+52 55 2378 5783', href: 'tel:+525523785783' },
          { icon: MapPin, label: 'Sede', value: 'Deportivo Xochimilco', href: '/contact' },
        ].map((item) => {
          const internal = item.href.startsWith('/')
          const Tag = internal ? Link : 'a'
          return (
            <Tag key={item.label} {...(internal ? { to: item.href } : { href: item.href })} className="rounded-3xl bg-white p-7 ring-1 ring-leaf/10 hover:shadow-md">
              <item.icon className="h-5 w-5 text-leaf" aria-hidden="true" />
              <p className="mt-4 text-sm text-mist">{item.label}</p>
              <p className="mt-1 font-serif text-2xl">{item.value}</p>
            </Tag>
          )
        })}
      </div>
    </section>
  </div>
)

export default About
